import { CardOrder } from "ts/api/order";
import { ReactNode } from "react";
import formatPrice from "tools/formats/formatPrice";
import { getDiscountAmount } from "view/apps/Exhibitor/pages/Cart/helpers";
import { parseBig } from "tools/bigJs";
import Big from "big.js";
import { IShow } from "@evennt-tech/api-v2-core";
import { UseTranslateResult } from "@tolgee/react";

type TranslateFn = UseTranslateResult["t"];

export type SummaryRow = {
  label: string;
  value: ReactNode;
};

const getGroupedOrderFees = ({ products }: CardOrder) => {
  const uniqueFees = new Map<string, string>();
  products.forEach(({ fees = [] }) => {
    fees.forEach(({ id, name }) => {
      if (!uniqueFees.has(id)) {
        uniqueFees.set(id, name);
      }
    });
  });

  const initFeeMap = Array.from(uniqueFees.keys()).reduce<Map<string, number>>(
    (acc, feeId) => {
      acc.set(feeId, 0);
      return acc;
    },
    new Map()
  );

  const feeAmountMap = products.reduce((acc, product) => {
    product.fees?.forEach(({ id, amountWithFees = 0 }) => {
      acc.set(id, (acc.get(id) ?? 0) + amountWithFees);
    });

    return acc;
  }, initFeeMap);

  return Array.from(feeAmountMap.entries()).reduce<Record<string, number>>(
    (acc, [id, amount]) => {
      const feeName = uniqueFees.get(id);
      if (feeName) {
        acc[feeName] = amount;
      }

      return acc;
    },
    {}
  );
};

export const getOrderSummaryRows = (
  show: IShow,
  order: CardOrder,
  t: TranslateFn
): SummaryRow[] => {
  const uniqueCompanyById = order.products.reduce<Record<string, string>>(
    (acc, product) => {
      if (!acc[product.owner.id]) {
        acc[product.owner.id] = product.owner.companyName;
      }

      return acc;
    },
    {}
  );
  const uniqueCompanyNames = Object.values(uniqueCompanyById).join(", ");

  const { salesTaxName: taxName, salesTaxPercentage: taxPercentage = 0 } = show;

  const taxCoefficient = taxPercentage / 100;
  const totalAmount = order.paymentDetails.amount;
  const taxAmount = parseBig(new Big(totalAmount).times(taxCoefficient));
  const discountAmount = getDiscountAmount(order.products, true);

  const feeByName = getGroupedOrderFees(order);
  const discountRows: SummaryRow[] = discountAmount
    ? [
        {
          label: t("exhibitor.cart.payment.summary.discount"),
          value: formatPrice(discountAmount, show.currency),
        },
      ]
    : [];

  return [
    {
      label: t("exhibitor.cart.payment.summary.supplier"),
      value: uniqueCompanyNames,
    },
    ...Object.entries(feeByName).map(([name, amount]) => ({
      label: name,
      value: formatPrice(amount, show.currency),
    })),
    {
      label: `${taxName} (${taxPercentage}%)`,
      value: taxAmount
        ? formatPrice(taxAmount, show.currency)
        : t("common.not_available"),
    },
    ...discountRows,
    {
      label: t("exhibitor.cart.payment.summary.total_cost"),
      value: formatPrice(totalAmount, show.currency),
    },
  ];
};
